import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone, PLATFORM_ID, signal } from '@angular/core';
import type Keycloak from 'keycloak-js';

export const AUTH_CALLBACK_PATH = '/logging-in';
const RETURN_PATH_KEY = 'imagise.auth.return-path';
const SESSION_KEY = 'imagise.auth.signed-in';
const AUTH_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly user = signal<string | null>(null);
  readonly pending = signal(false);
  readonly error = signal<string | null>(null);

  readonly #document = inject(DOCUMENT);
  readonly #browser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly #zone = inject(NgZone);
  #client?: Keycloak;
  #initialization?: Promise<boolean>;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.#disposeClient());
  }

  /** Process the callback before Angular routing. Anonymous visits make no IdP requests. */
  initialize(): Promise<boolean> {
    if (!this.#browser) return Promise.resolve(false);
    if (this.#initialization) return this.#initialization;

    this.pending.set(true);
    this.#initialization = this.#zone.runOutsideAngular(() => this.#initialize());
    return this.#initialization;
  }

  async login(): Promise<void> {
    if (!this.#browser || this.pending()) return;
    this.pending.set(true);
    this.error.set(null);
    if (!this.#client) this.#initialization = undefined;

    await this.initialize();
    const client = this.#client;
    if (!client) return;

    this.pending.set(true);
    this.error.set(null);
    this.#rememberReturnPath();
    try {
      await client.login({
        redirectUri: this.#window.location.origin + AUTH_CALLBACK_PATH,
        locale: this.#document.documentElement.lang || 'en',
      });
    } catch {
      this.error.set('AUTH.LOGIN_FAILED');
      this.pending.set(false);
    }
  }

  async logout(): Promise<void> {
    const client = this.#client;
    if (!this.#browser || !client || this.pending()) return;
    this.pending.set(true);
    this.error.set(null);
    this.#store(SESSION_KEY, null);
    this.#store(RETURN_PATH_KEY, null);
    try {
      await client.logout({ redirectUri: this.#window.location.origin + '/' });
    } catch {
      this.error.set('AUTH.LOGOUT_FAILED');
    } finally {
      this.#disposeClient();
      this.user.set(null);
      this.pending.set(false);
    }
  }

  /** Return a fresh in-memory token for a user-initiated authenticated request. */
  async getAccessToken(): Promise<string | null> {
    const client = this.#client;
    if (!this.#browser || !client?.authenticated || this.pending()) return null;
    await this.#zone.runOutsideAngular(() => this.#refresh(client));
    return client === this.#client && client.authenticated ? client.token ?? null : null;
  }

  takeReturnPath(consume = true): string {
    const saved = this.#read(RETURN_PATH_KEY);
    if (consume) this.#store(RETURN_PATH_KEY, null);
    if (!saved || !this.#browser) return '/';
    try {
      const target = new URL(saved, this.#window.location.origin);
      if (target.origin !== this.#window.location.origin || target.pathname === AUTH_CALLBACK_PATH) return '/';
      return target.pathname + target.search + target.hash;
    } catch {
      return '/';
    }
  }

  get #window(): Window {
    const view = this.#document.defaultView;
    if (!view) throw new Error('Authentication requires a browser');
    return view;
  }

  async #initialize(): Promise<boolean> {
    const callback = this.#window.location.pathname === AUTH_CALLBACK_PATH;
    // This flag only requests a fresh SSO check; it is never proof of authentication.
    const hadSession = this.#read(SESSION_KEY) === 'true';
    const restoreSession = !callback && hadSession;
    if (restoreSession) this.#rememberReturnPath();

    try {
      const { default: KeycloakClient } = await import('keycloak-js');
      const client = new KeycloakClient({
        url: 'https://id.containerize.app',
        realm: 'imagise-app',
        clientId: 'imagise-app-client',
      });
      this.#client = client;
      client.onAuthSuccess = client.onAuthRefreshSuccess = () => this.#syncUser(client);
      client.onAuthLogout = () =>
        this.#zone.run(() => {
          this.user.set(null);
          this.#store(SESSION_KEY, null);
        });
      client.onTokenExpired = () => void this.#refresh(client);

      const authenticated = await this.#bounded(
        client,
        client.init({
          flow: 'standard',
          pkceMethod: 'S256',
          responseMode: 'fragment',
          scope: 'openid profile email',
          checkLoginIframe: false,
          redirectUri: this.#window.location.origin + AUTH_CALLBACK_PATH,
          ...(restoreSession ? { onLoad: 'check-sso' as const } : {}),
        })
      );
      this.#syncUser(client);
      // An expired SSO session is a normal anonymous return to the public editor.
      if (callback && !authenticated && !hadSession) this.#zone.run(() => this.error.set('AUTH.CALLBACK_FAILED'));
      return authenticated;
    } catch {
      this.#disposeClient();
      this.#store(SESSION_KEY, null);
      this.#zone.run(() => {
        this.user.set(null);
        this.error.set(callback ? 'AUTH.CALLBACK_FAILED' : 'AUTH.LOGIN_FAILED');
      });
      return false;
    } finally {
      // Also clean callbacks with missing/invalid state that the SDK leaves unauthenticated.
      if (callback) this.#window.history.replaceState(this.#window.history.state, '', AUTH_CALLBACK_PATH);
      this.#zone.run(() => this.pending.set(false));
    }
  }

  #syncUser(client: Keycloak): void {
    if (client !== this.#client) return;
    const claims = client.idTokenParsed ?? client.tokenParsed;
    const name = [claims?.['name'], claims?.['preferred_username'], client.subject].find(
      value => typeof value === 'string' && value.trim()
    );
    this.#zone.run(() => {
      this.user.set(client.authenticated ? (name ?? 'Imagise') : null);
      this.#store(SESSION_KEY, client.authenticated ? 'true' : null);
    });
  }

  async #refresh(client: Keycloak): Promise<void> {
    if (client !== this.#client) return;
    try {
      await this.#bounded(client, client.updateToken(30));
      this.#syncUser(client);
    } catch {
      if (client !== this.#client) return;
      this.#disposeClient();
      this.#store(SESSION_KEY, null);
      this.#zone.run(() => {
        this.user.set(null);
        this.error.set('AUTH.SESSION_EXPIRED');
      });
    }
  }

  #bounded<T>(client: Keycloak, operation: Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = this.#window.setTimeout(() => reject(new Error('Authentication timed out')), AUTH_TIMEOUT_MS);
      const finish = () => {
        this.#window.clearTimeout(timer);
        // A late SDK response must not restore a session after timeout/logout.
        if (client !== this.#client) client.clearToken();
      };
      void operation.then(
        value => {
          finish();
          resolve(value);
        },
        () => {
          finish();
          reject(new Error('Authentication request failed'));
        }
      );
    });
  }

  #disposeClient(): void {
    const client = this.#client;
    this.#client = undefined;
    if (!client) return;
    client.onAuthSuccess = client.onAuthRefreshSuccess = client.onAuthLogout = client.onTokenExpired = undefined;
    client.clearToken();
  }

  #rememberReturnPath(): void {
    const { pathname, search, hash } = this.#window.location;
    if (pathname !== AUTH_CALLBACK_PATH) this.#store(RETURN_PATH_KEY, pathname + search + hash);
  }

  #read(key: string): string | null {
    try {
      return this.#browser ? this.#window.sessionStorage.getItem(key) : null;
    } catch {
      return null;
    }
  }

  #store(key: string, value: string | null): void {
    if (!this.#browser) return;
    try {
      if (value === null) this.#window.sessionStorage.removeItem(key);
      else this.#window.sessionStorage.setItem(key, value);
    } catch {
      // Login still works when storage is unavailable; return to the home page.
    }
  }
}
